import { busConfigs, busConfigsTick } from '../../../../../../stores/bus-config';
import { setFormApi, setFormValidateFunc, pState, lastProcess, nextProcess } from '../../store';
import { Form, FormApi, If, onMount, reactive, watch } from 'cdui-js';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import { OpenFormItem } from '../../components/OpenFormItem';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { SelectBox } from '../../../../components/SelectBox';
import { DiFiCheckBox } from '../../../../../../components/DiFi/DiFiCheckBox';
import { CheckBox } from '../../../../components/CheckBox';
import { LastNextBtns } from '../../components/LastNextBtns';
import { isOpenProgressReEdit } from '../../reEditMode';
import { saveCareer } from '../../../../../../api/open-account';
import type { AgreementSceneDocItem } from '../../../../../../api/agreement-doc';
import { openAgreementDoc } from '../../../../../../lib/agreement-doc-view';
import { agreementLinkLabel } from '../../../../../../lib/agreementLinkLabel';
import { loadAgreementDocsByCodesWithPreload } from '../../../../../../lib/agreement-pdf-cache';
import { showToast, showToastError } from '../../../../components/Toast';
import i18n from '../../../../../../i18n';
import {
  clearAllExperienceYears,
  getInvestmentExperienceOptionsCopy,
  isNoneExperienceType,
  resolveExperienceYearField,
  type InvestmentExperienceYearField,
} from './investmentExperienceMap';

const SCENE_OPEN_ACCOUNT_INVEST_EXPERIENCE = 'SCENE_OPEN_ACCOUNT_INVEST_EXPERIENCE';
/** 衍生工具产品的认识及风险披露声明 */
const DOC_DERIVATIVES = 'AGR003';
/** 投资目标字典「其它」选项 code */
const INVESTMENT_PURPOSE_OTHER = 'INVESTMENT_PURPOSE_OTHER';

const toArr = (v: string): string[] =>
  v
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

const isFlagOn = (v: unknown) => v === 1 || v === '1';
const toFlag01 = (on: boolean): 0 | 1 => (on ? 1 : 0);

const toggleCsv = (csv: string, val: string | number | undefined) => {
  if (val === undefined) return csv;
  const next = String(val);
  const arr = toArr(csv);
  return arr.includes(next) ? arr.filter((item) => item !== next).join(',') : [...arr, next].join(',');
};

const clearDerivativeQualify = () => {
  const form = pState.investmentExpForm;
  form.hasDerivativeTraining = 0;
  form.hasDerivativeWorkExperience = 0;
  form.hasDerivativeTrade5times = 0;
};

const findTypeOption = (code: string) =>
  (busConfigs.investmentExperienceType || []).find((o) => String(o.value) === String(code));

const hasInvestmentTargetOther = () =>
  toArr(pState.investmentExpForm.investmentTarget).includes(INVESTMENT_PURPOSE_OTHER);

/** 与页内下一步一致；EditErrorInfo 退回修改走同一套 payload */
export const buildInvestmentExpPayload = () => {
  const data = { ...pState.investmentExpForm };
  if (!hasInvestmentTargetOther()) {
    data.investmentPurposeOther = '';
  }
  return { ...data, progress: 7 };
};

export const InvestmentExp = (props) => {
  let formApi: FormApi = null;
  const state = reactive({
    derivativesDoc: null as AgreementSceneDocItem | null,
    derivativeAgreed: false,
    dictRev: 0,
  });
  // 衍生品协议勾选 DOM：校验失败时滚动到未勾选项
  let derivativeRef: HTMLDivElement | null = null;

  watch(
    () => busConfigsTick.v,
    (v) => {
      state.dictRev = v;
    },
  );

  const experienceTypeOptions = () => {
    void state.dictRev;
    return busConfigs.investmentExperienceType || [];
  };

  const objectiveOptions = () => {
    void state.dictRev;
    return busConfigs.investmentObjective || [];
  };

  const experienceYearOptions = (yearField: InvestmentExperienceYearField) => {
    void state.dictRev;
    return getInvestmentExperienceOptionsCopy(yearField, busConfigs.investmentExperience);
  };

  /** 有意买卖时须确认风险披露；三条认知勾选非必选。须走 formValidateFunc，EditErrorInfo 退回修改不会调本页 next */
  const validateDerivativeAgree = async () => {
    if (pState.investmentExpForm.intendTradeDerivative == '1' && !state.derivativeAgreed) {
      showToast(i18n.OpenProgress.Info.Investment.ToastDerivativeAgree);
      derivativeRef?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    return true;
  };

  onMount(() => {
    setFormValidateFunc({
      callback: validateDerivativeAgree,
    });

    loadAgreementDocsByCodesWithPreload({
      sceneCode: SCENE_OPEN_ACCOUNT_INVEST_EXPERIENCE,
      docCodes: [DOC_DERIVATIVES],
    })
      .then(({ docMap }) => {
        state.derivativesDoc = docMap[DOC_DERIVATIVES] ?? null;
      })
      .catch(() => {
        // 接口失败时保留 i18n 兜底文案，点击时 toast
      });
  });

  const onToggleExperienceType = (code: string, label: unknown) => {
    const form = pState.investmentExpForm;
    const selected = toArr(form.investmentExperienceTypes);
    const checked = selected.includes(code);

    if (checked) {
      // 取消勾选：移除类型并清空对应年资
      form.investmentExperienceTypes = selected.filter((c) => c !== code).join(',');
      const field = resolveExperienceYearField(code, label);
      if (field) form[field] = '';
      return;
    }

    if (isNoneExperienceType(code, label)) {
      // 「没有」与其它类型互斥
      form.investmentExperienceTypes = code;
      clearAllExperienceYears(form);
      return;
    }

    // 勾选具体类型时，去掉「没有」
    const noneCodes = selected.filter((c) => {
      const opt = findTypeOption(c);
      return isNoneExperienceType(c, opt?.label);
    });
    const next = selected.filter((c) => !noneCodes.includes(c));
    next.push(code);
    form.investmentExperienceTypes = next.join(',');
  };

  const hasTargetOther = () => hasInvestmentTargetOther();

  return (

    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">
        {i18n.OpenProgress.Info.Investment.Title}
        {props.stepText}
      </div>
      <FieldReEditHint errorField="riskLevel" class="margin-b-L" />

      <Form
        data={pState.investmentExpForm}
        rules={{}}
        align="top"
        api={(api) => {
          formApi = api;
          setFormApi(api);
        }}
      >
        <OpenFormItem
          label={i18n.OpenProgress.Info.Investment.LabelKnowledge ?? ''}
          field="investmentKnowledge"
          required
        >
          <SelectBox
            title={i18n.OpenProgress.Info.Investment.LabelKnowledge}
            value={pState.investmentExpForm.investmentKnowledge}
            dict="investmentKnowledge"
            change={(e) => (pState.investmentExpForm.investmentKnowledge = e)}
          />
        </OpenFormItem>

        {/*
          年资 OpenFormItem 必须与经验类型 FormItem 同级：
          cdui Form.validate 遇到 FORM_ITEM 后不再递归子节点，嵌套会导致年资「不能为空」出不来。
          勾选列表放在类型 FormItem 外，与年资交错，保证年资下拉紧跟对应勾选项下方。
        */}
        <div>
          <OpenFormItem
            label={i18n.OpenProgress.Info.Investment.LabelExperienceType ?? ''}
            field="investmentExperienceTypes"
            required
          ></OpenFormItem>

          <div class="flex-column gap-12px margin-t-XS">
            {experienceTypeOptions().map((o) => {
              const code = String(o.value);
              const checked = toArr(pState.investmentExpForm.investmentExperienceTypes).includes(
                code
              );
              const yearField = resolveExperienceYearField(code, o.label);
              const typeLabel = typeof o.label === 'function' ? o.label() : o.label;
              return (
                <div class="flex-column gap-8px">
                  <DiFiCheckBox
                    type="checkbox"
                    label={typeof o.label === 'function' ? undefined : o.label}
                    labelRender={typeof o.label === 'function' ? () => o.label() : undefined}
                    value={o.value}
                    checked={checked}
                    class="font-s-S color-D"
                    style={{ width: '100%' }}
                    onChange={() => onToggleExperienceType(code, o.label)}
                  />
                  {checked && yearField ? (
                    <OpenFormItem
                      label={typeLabel || ''}
                      field={yearField}
                      required
                      class="open-form-item-compact"
                    >
                      <SelectBox
                        title={i18n.OpenProgress.Info.Investment.PlaceholderYears}
                        placeholder={i18n.OpenProgress.Info.Investment.PlaceholderYears}
                        value={pState.investmentExpForm[yearField]}
                        options={experienceYearOptions(yearField)}
                        change={(e) => (pState.investmentExpForm[yearField] = e)}
                      />
                    </OpenFormItem>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <OpenFormItem
          label={i18n.OpenProgress.Info.Investment.LabelTarget ?? ''}
          field="investmentTarget"
          required
        >
          <div class="flex-row flex-wrap margin-t-XS" style={{ 'row-gap': '18px', 'column-gap': '16px' }}>
            {objectiveOptions().map((o) => {
              const val = String(o.value);
              return (
                <DiFiCheckBox
                  type="checkbox"
                  label={typeof o.label === 'function' ? undefined : o.label}
                  labelRender={typeof o.label === 'function' ? () => o.label() : undefined}
                  value={o.value}
                  checked={toArr(pState.investmentExpForm.investmentTarget).includes(val)}
                  class="font-s-S color-D"
                  style={{ width: 'calc(50% - 8px)' }}
                  onChange={(v) => {
                    pState.investmentExpForm.investmentTarget = toggleCsv(
                      pState.investmentExpForm.investmentTarget,
                      v
                    );
                    if (!hasTargetOther()) {
                      pState.investmentExpForm.investmentPurposeOther = '';
                    }
                  }}
                />
              );
            })}
          </div>
        </OpenFormItem>

        <If when={hasTargetOther()}>
          <OpenFormItem label="" field="investmentPurposeOther" required>
            <DiFiInput
              type="textarea"
              rows={3}
              shellClass="f-input-2"
              maxLength={80}
              placeholder={i18n.OpenProgress.Info.Investment.TargetOtherPlaceholder}
              value={pState.investmentExpForm.investmentPurposeOther}
              onUpdateValue={(v) => (pState.investmentExpForm.investmentPurposeOther = v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem
          label={i18n.OpenProgress.Info.Investment.IntendTradeDerivative ?? ''}
          field="intendTradeDerivative"
          required
        >
          <SelectBox
            title={i18n.OpenProgress.Info.Investment.IntendTradeDerivative}
            value={pState.investmentExpForm.intendTradeDerivative}
            options={[
              { label: i18n.OpenProgress.Info.Investment.IntendTradeNo, value: '0' },
              { label: i18n.OpenProgress.Info.Investment.IntendTradeYes, value: '1' },
            ]}
            change={(e) => {
              pState.investmentExpForm.intendTradeDerivative = e;
              if (e !== '1') {
                state.derivativeAgreed = false;
                clearDerivativeQualify();
              }
            }}
          />
        </OpenFormItem>

        {pState.investmentExpForm.intendTradeDerivative == '0' && (
          <div class="font-s-XS color-error margin-b-S">{i18n.OpenProgress.Info.Investment.NoDerivativeWarning}</div>
        )}

        {pState.investmentExpForm.intendTradeDerivative == '1' && (
          <>
            <div class="bg-c-D round padding font-s-XS margin-b-S">
              <div>{i18n.OpenProgress.Info.Investment.DerivativeCategoriesTitle}</div>
              <div>{i18n.OpenProgress.Info.Investment.DerivativeCat1}</div>
            </div>

            <div
              class="flex-column gap-12px font-s-S color margin-b-S"
              style={{ 'line-height': '18px' }}
            >
              <CheckBox
                labelPaddingY={4}
                active={isFlagOn(pState.investmentExpForm.hasDerivativeTraining)}
                change={(on) => {
                  pState.investmentExpForm.hasDerivativeTraining = toFlag01(on);
                }}
              >
                {i18n.OpenProgress.Info.Investment.DerivativeQualifyTraining}
              </CheckBox>
              <CheckBox
                labelPaddingY={4}
                active={isFlagOn(pState.investmentExpForm.hasDerivativeWorkExperience)}
                change={(on) => {
                  pState.investmentExpForm.hasDerivativeWorkExperience = toFlag01(on);
                }}
              >
                {i18n.OpenProgress.Info.Investment.DerivativeQualifyWork}
              </CheckBox>
              <CheckBox
                labelPaddingY={4}
                active={isFlagOn(pState.investmentExpForm.hasDerivativeTrade5times)}
                change={(on) => {
                  pState.investmentExpForm.hasDerivativeTrade5times = toFlag01(on);
                }}
              >
                {i18n.OpenProgress.Info.Investment.DerivativeQualifyTrade5}
              </CheckBox>
            </div>

            <div
              ref={(el) => {
                derivativeRef = el;
              }}
              class="font-s-XS color"
              style={{ 'margin-top': '100px' }}
            >
              <CheckBox
                type={2}
                class="padding-y-S"
                style={{ 'margin-left': '-8px', 'padding-left': '8px' }}
                active={state.derivativeAgreed}
                change={(e) => {
                  state.derivativeAgreed = e;
                }}
              >
                {i18n.OpenProgress.Info.Investment.DerivativeAgreeBefore}
                <span
                  data-nav-only
                  class="agreement-link"
                  onclick={(e) => {
                    e.stopPropagation();
                    openAgreementDoc(state.derivativesDoc);
                  }}
                >
                  {agreementLinkLabel(
                    state.derivativesDoc?.name,
                    i18n.OpenProgress.Info.Investment.DerivativeAgreeLink,
                  )}
                </span>
                {i18n.OpenProgress.Info.Investment.DerivativeAgreeAfter}
              </CheckBox>
            </div>
          </>
        )}
      </Form>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            try {
              const valid = await formApi.validate();
              if (!valid) {
                formApi?.scrollToError?.();
                return;
              }
              // Portal 已跑 formValidateFunc；此处再拦一次，避免仅调 next 时漏校验
              if (!(await validateDerivativeAgree())) return;
              await saveCareer(buildInvestmentExpPayload());
              nextProcess();
            } catch (e) {
              showToastError(e);
            }
          }}
        />
      </If>
    </div>
  );
};
